import React, { useEffect, useRef } from 'react';
import { loadFFFooterScript } from '~/app/utils/loadFFWidgetScript';

/**
 * Mounts the CDN-delivered FFFooter into a local container.
 *
 * Props are optional: omitted, the bundle renders the official site's
 * default footer content for the region/preset.
 *
 * @param {object} props
 * @param {string} props.region - 'us' | 'cn'
 * @param {string} [props.preset] - 'legacy' (default) | 'ffai' | full CMS title
 * @param {object} [props.props] - Footer props passed through to FFFooter.mount
 * @param {string} [props.className]
 * @param {string} props.scriptUrl - Absolute URL of ff-footer.js
 */
export default function RemoteFFFooter({ region, preset, props: footerProps, className, scriptUrl }) {
  const containerRef = useRef(null);
  // Content fingerprint so parent re-renders with identical props do not remount
  const propsKey = JSON.stringify(footerProps ?? null);

  useEffect(() => {
    let cancelled = false;
    const el = containerRef.current;

    if (!el || !scriptUrl) {
      return undefined;
    }

    const mountProps = footerProps;

    loadFFFooterScript(scriptUrl)
      .then((FFFooter) => {
        if (cancelled || !FFFooter?.mount) {
          return;
        }
        FFFooter.mount(el, { region, preset, props: mountProps, className });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[RemoteFFFooter] failed to load/mount', err);
      });

    return () => {
      cancelled = true;
      if (el && window.FFFooter?.unmount) {
        try {
          window.FFFooter.unmount(el);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[RemoteFFFooter] unmount failed', err);
        }
      }
    };
    // propsKey tracks footerProps content; do not depend on object identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, preset, className, scriptUrl, propsKey]);

  return <div ref={containerRef} data-ff-footer />;
}
